import { ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import { ReviewEntity } from '@/entities/review.entity';

const toTruthyString = (value: string | undefined | null) => (value || '').trim();
const hasTruthyString = (value: string | undefined | null) => !!toTruthyString(value);
const hasAiResponse = (review: ReviewEntity) => review.aiApiResponse !== null;
const getAnswerText = (review: ReviewEntity) => toTruthyString(review.answer?.text);
const getAnswerUpdatedDate = (review: ReviewEntity) => toTruthyString(review.answer?.updatedDate);

/**
 * Selection behavior contract:
 * 1) Row checkbox mutates only one id.
 * 2) Header checkbox mutates only currently visible page ids.
 * 3) Global "select all" button mutates ids matched by current filters (not only current page).
 * 4) Reconcile is intersection-only: selected := selected ∩ matchedByCurrentFilters.
 * 5) Reconcile must run after filter/sort changes.
 * 6) Reconcile must also run when review answer/AI state changes and active filters depend on those fields.
 */

/**
 * Filters that depend on answer/AI status and therefore require reconcile when those fields change.
 */
export const hasAnswerOrAiSensitiveFilters = (filters: ReviewSearchParamsDto): boolean => {
    return Boolean(
        filters.convertedStatus ||
        filters.hasAnswerText ||
        hasTruthyString(filters.answerText) ||
        hasTruthyString(filters.answerUpdatedDate) ||
        filters.hasAiResponse,
    );
};

/**
 * Detects state changes that can affect filter matching for rules based on answer status/content or AI analysis.
 */
export const didReviewAnswerOrAiStateChange = (previousReview: ReviewEntity, nextReview: ReviewEntity): boolean => {
    if (previousReview.convertedStatus !== nextReview.convertedStatus) {
        return true;
    }

    if (getAnswerText(previousReview) !== getAnswerText(nextReview)) {
        return true;
    }

    if (getAnswerUpdatedDate(previousReview) !== getAnswerUpdatedDate(nextReview)) {
        return true;
    }

    if (hasAiResponse(previousReview) !== hasAiResponse(nextReview)) {
        return true;
    }

    return false;
};

/**
 * Rule 6 trigger: reconcile selection after answer/AI state changes,
 * but only if current filters can be affected by those changes.
 */
export const shouldReconcileSelectionAfterAnswerOrAiStateChange = (params: {
    previousReview: ReviewEntity;
    nextReview: ReviewEntity;
    filters: ReviewSearchParamsDto;
}): boolean => {
    if (!hasAnswerOrAiSensitiveFilters(params.filters)) {
        return false;
    }

    return didReviewAnswerOrAiStateChange(params.previousReview, params.nextReview);
};
